import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Card,
  CardContent,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Work as JobIcon,
  ContentCopy as CloneIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { SAP_COLORS, SAP_SHADOWS, SAP_TYPOGRAPHY } from '../theme/sapDesignSystem';
import { fetchClients } from '../api/features/clientData/clientApi';
import { fetchJobDetails, fetchJobDetailsCount, fetchJobNames } from '../api/features/jobDetail/jobDetailApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { SAPIconButton, PageHeader } from '../components';
import { api } from '../api/fetchWrapper';
import { API_ENDPOINTS, getEndpoint } from '../api/apiConstants';

// Clone Job Components - matching JobDetails table and search filter structure

export default function CloneJob() {
  const { clientCode } = useClientCode();
  const isAdmin = clientCode === 'admin';

  const [client, setClient] = useState<any>(null);
  const [job, setJob] = useState<any>(null);
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [jobOptions, setJobOptions] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasSearched, setHasSearched] = useState(false);
  const [userClientId, setUserClientId] = useState<string | null>(null);

  // Clone dialog state
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [selectedJobToClone, setSelectedJobToClone] = useState<any>(null);
  const [newClientId, setNewClientId] = useState<string>('');
  const [newJobName, setNewJobName] = useState<string>('');
  const [cloning, setCloning] = useState(false);
  const [jobNameError, setJobNameError] = useState<string>('');

  // Fetch client options (admin only) - exactly like JobDetail
  useEffect(() => {
    if (isAdmin) {
      fetchClients({ clientCode, firstResult: -1, maxResult: 0 })
        .then((clients) => setClientOptions(clients.map((c: any) => ({ label: c.name, value: c.id, code: c.code }))))
        .catch(() => setClientOptions([]));
    } else {
      // For non-admin, fetch their clientId from the client list API
      fetchClients({ clientCode, firstResult: -1, maxResult: 0 })
        .then((clients) => {
          if (clients && clients.length > 0) {
            setUserClientId(clients[0].id);
          }
        })
        .catch(() => setUserClientId(null));
    }
  }, [clientCode, isAdmin]);

  // Fetch job name options - exactly like JobDetail
  useEffect(() => {
    if (isAdmin) {
      if (client && client.value) {
        fetchJobDetails({ clientCode, clientId: client.value, firstResult: -1, maxResult: 0 })
          .then((jobs) => setJobOptions(jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))))
          .catch(() => setJobOptions([]));
      } else {
        // Fetch all jobs if client is not selected
        fetchJobDetails({ clientCode, firstResult: -1, maxResult: 0 })
          .then((jobs) => setJobOptions(jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))))
          .catch(() => setJobOptions([]));
      }
      setJob(null);
    } else if (userClientId) {
      fetchJobDetails({ clientCode, clientId: Number(userClientId), firstResult: -1, maxResult: 0 })
        .then((jobs) => setJobOptions(jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))))
        .catch(() => setJobOptions([]));
      setJob(null);
    }
  }, [client, clientCode, isAdmin, userClientId]);

  // Fetch job details and count for pagination - exactly like JobDetail but with clone-specific parameters
  const fetchData = async (pageOverride?: number) => {
    setLoading(true);
    setError(null);
    try {
      let clientId = undefined;
      if (isAdmin) {
        clientId = client ? client.value : undefined;
      } else {
        clientId = userClientId;
      }
      const jobName = job?.value || undefined;
      
      // Use the exact same API calls as JobDetail but with clone-specific parameters
      const [data, count] = await Promise.all([
        fetchJobDetails({
          clientCode,
          firstResult: (pageOverride ?? page) * rowsPerPage,
          maxResult: rowsPerPage,
          clientId,
          jobName,
        }),
        fetchJobDetailsCount({
          clientCode,
          clientId,
          jobName,
        })
      ]);
      setRows(data);
      setRowCount(count);
    } catch (err: any) {
      setError('Failed to load job details for cloning');
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle page change - exactly like JobDetail
  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    fetchData(newPage);
  };

  // Reset page to 0 on new search - require client selection for admin
  const handleSearch = () => {
    if (isAdmin && !client?.value) {
      toast.warning('Please select a client before searching. Clone is only allowed when a client is selected.');
      return;
    }
    setLoading(true);
    setError(null);
    setPage(0);
    setHasSearched(true);

    const searchParams = {
      clientCode,
      firstResult: 0,
      maxResult: rowsPerPage,
      clientId: isAdmin ? (client?.value || null) : userClientId,
      jobName: job?.value || null,
    };

    const countParams = {
      clientCode,
      clientId: isAdmin ? (client?.value || null) : userClientId,
      jobName: job?.value || null,
    };

    Promise.all([
      fetchJobDetails(searchParams),
      fetchJobDetailsCount(countParams),
    ])
      .then(([response, totalCount]) => {
        setRows(response || []);
        setRowCount(totalCount);
      })
      .catch((err) => {
        setError(err.message || 'Failed to fetch job details');
        setRows([]);
        setRowCount(0);
      })
      .finally(() => setLoading(false));
  };

  // Handle clone button click - allow only when client is selected and search has been done
  const handleCloneClick = (jobData: any) => {
    if (isAdmin && !client?.value) {
      toast.warning('Please select a client and search before cloning.');
      return;
    }
    if (!hasSearched) {
      toast.warning('Please search with a client selected before cloning.');
      return;
    }
    setSelectedJobToClone(jobData);
    setNewClientId('');
    setNewJobName('');
    setJobNameError('');
    setCloneDialogOpen(true);
  };

  // Handle clone job
  const handleCloneJob = async () => {
    // Validate required fields
    if (!selectedJobToClone || !newClientId || !newJobName) {
      toast.error('Please fill all required fields');
      return;
    }

    // Validate job name format
    const jobNameValidationError = validateJobName(newJobName);
    if (jobNameValidationError) {
      setJobNameError(jobNameValidationError);
      toast.error(jobNameValidationError);
      return;
    }

    setCloning(true);
    try {
      const payload = {
        originalJobName: selectedJobToClone[2], // jobName is at index 2
        originalClientId: selectedJobToClone.clientId || (isAdmin ? (client?.value || 0) : Number(userClientId)),
        newClientId: Number(newClientId),
        newJobName: newJobName,
      };
      
      // Use the proper API wrapper and endpoint construction
      const endpoint = getEndpoint(clientCode, API_ENDPOINTS.CLONE_JOB.CLONE);
      await api.post(endpoint, payload);
      
      toast.success('Job cloned successfully!');
      setCloneDialogOpen(false);
      fetchData(page); // Refresh the data
    } catch (err: any) {
      toast.error(err.message || 'Failed to clone job');
    } finally {
      setCloning(false);
    }
  };

  // Validation function for job names - only allow letters and underscores
  const validateJobName = (name: string): string => {
    if (!name.trim()) {
      return 'Job name is required';
    }
    
    // Only allow letters (uppercase and lowercase) and underscores
    const validJobNamePattern = /^[a-zA-Z_]+$/;
    
    if (!validJobNamePattern.test(name)) {
      return 'Job name can only contain letters and underscores';
    }
    
    // Additional checks
    if (name.length < 2) {
      return 'Job name must be at least 2 characters long';
    }
    
    if (name.length > 50) {
      return 'Job name must be less than 50 characters';
    }
    
    if (name.startsWith('_') || name.endsWith('_')) {
      return 'Job name cannot start or end with underscore';
    }
    
    return ''; // No error
  };

  // Handle job name input change with validation
  const handleJobNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewJobName(value);
    
    if (value) {
      const error = validateJobName(value);
      setJobNameError(error);
    } else {
      setJobNameError('');
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Page Header */}
      <PageHeader 
        title="Clone Jobs"
        description="Search and clone jobs to different clients"
      />

      {/* Search Section - matching JobDetails exactly */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
            {isAdmin && (
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <Autocomplete
                  options={clientOptions}
                  getOptionLabel={(option) => option.label}
                  value={client}
                  onChange={(_, newValue) => setClient(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Client" />
                  )}
                />
              </Box>
            )}

            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <Autocomplete
                options={jobOptions}
                getOptionLabel={(option) => option.label}
                value={job}
                onChange={(_, newValue) => setJob(newValue)}
                disabled={!client}
                renderInput={(params) => (
                  <TextField {...params} label="Job Name" />
                )}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <SAPIconButton
                icon="search"
                variant="primary"
                onClick={handleSearch}
                disabled={loading}
                aria-label="Search"
              />
              <SAPIconButton
                icon="reset"
                variant="secondary"
                onClick={() => { 
                  setClient(null); 
                  setJob(null); 
                  setRows([]); 
                  setRowCount(0); 
                  setPage(0); 
                  setHasSearched(false);
                }}
                disabled={loading}
                aria-label="Reset"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert - matching JobDetails */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Data Section */}
      {hasSearched && (
        <>
          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Data Display */}
          {!loading && !error && (
            <>
              {/* Table Container */}
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table sx={{ minWidth: 900 }} stickyHeader aria-label="clone job table">
                  <TableHead>
                    <TableRow>
                      <TableCell>S.No.</TableCell>
                      <TableCell>Job Name</TableCell>
                      <TableCell align="center">Clone</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        No jobs found. Try searching with different criteria.
                      </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, idx) => (
                        <TableRow key={row[0] || idx}>
                          <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                          <TableCell>{row[2] || 'N/A'}</TableCell>
                          <TableCell align="center">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleCloneClick(row)}
                              startIcon={<CloneIcon />}
                              disabled={isAdmin && !client?.value}
                              sx={{ 
                                backgroundColor: '#1976d2',
                                '&:hover': { 
                                  backgroundColor: '#1565c0',
                                },
                                textTransform: 'none',
                                fontWeight: 500,
                              }}
                            >
                              Clone
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
          </TableContainer>

          {/* Pagination */}
          {rows.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {/* Pagination Info */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {(page * rowsPerPage) + 1}-{Math.min((page * rowsPerPage) + rows.length, rowCount)} of {rowCount} jobs
                </Typography>
              </Box>
              
              {/* Pagination Controls */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Rows per page</InputLabel>
                  <Select
                    value={rowsPerPage}
                    onChange={(event: SelectChangeEvent<number>) => {
                      const newRowsPerPage = Number(event.target.value);
                      setRowsPerPage(newRowsPerPage);
                      setPage(0);

                      const searchParams = {
                        clientCode,
                        firstResult: 0,
                        maxResult: newRowsPerPage,
                        clientId: isAdmin ? (client?.value || undefined) : userClientId,
                        jobName: job?.value || undefined,
                      };

                      const countParams = {
                        clientCode,
                        clientId: isAdmin ? (client?.value || undefined) : userClientId,
                        jobName: job?.value || undefined,
                      };

                      setLoading(true);
                      Promise.all([
                        fetchJobDetails(searchParams),
                        fetchJobDetailsCount(countParams)
                      ])
                        .then(([response, totalCount]) => {
                          setRows(response || []);
                          setRowCount(totalCount);
                        })
                        .catch((err) => {
                          setError(err.message || 'Failed to fetch job details');
                        })
                        .finally(() => {
                          setLoading(false);
                        });
                    }}
                    label="Rows per page"
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
                
                <Pagination
                  count={Math.ceil(rowCount / rowsPerPage)}
                  page={page + 1}
                  onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                    setPage(newPage - 1);
                    const searchParams = {
                      clientCode,
                      firstResult: (newPage - 1) * rowsPerPage,
                      maxResult: rowsPerPage,
                      clientId: isAdmin ? (client?.value || undefined) : userClientId,
                      jobName: job?.value || undefined,
                    };
                    setLoading(true);
                    fetchJobDetails(searchParams)
                      .then((response) => setRows(response || []))
                      .catch((err) => setError(err.message || 'Failed to fetch job details'))
                      .finally(() => setLoading(false));
                  }}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </Box>
          )}
            </>
          )}

          {/* Empty State */}
          {!loading && !error && rows.length === 0 && (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No jobs found. Try searching with different criteria.
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Initial Empty State */}
      {!hasSearched && (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No jobs found. Try searching with different criteria.
          </Typography>
        </Box>
      )}

      {/* Clone Dialog - Enhanced UI with source → destination flow */}
      <Dialog
        open={cloneDialogOpen}
        onClose={() => !cloning && setCloneDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: SAP_SHADOWS.modal,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            py: 2,
            px: 3,
            bgcolor: SAP_COLORS.sapGray1,
            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: SAP_COLORS.sapLightBlue,
                color: SAP_COLORS.sapBlue,
              }}
            >
              <CloneIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold, color: SAP_COLORS.sapGray7 }}>
                Clone Job
              </Typography>
              <Typography variant="caption" sx={{ color: SAP_COLORS.sapGray5 }}>
                Copy job to a different client
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => !cloning && setCloneDialogOpen(false)}
            disabled={cloning}
            sx={{
              color: SAP_COLORS.sapGray5,
              '&:hover': { bgcolor: SAP_COLORS.sapGray2 },
            }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Source Section - Clear display of job and client being cloned */}
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 1.5,
              bgcolor: SAP_COLORS.sapPaleBlue,
              borderColor: SAP_COLORS.sapGray2,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: SAP_COLORS.sapGray5, mb: 1.5, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Source — Job & Client being cloned
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: { xs: 1.5, sm: 3 },
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ color: SAP_COLORS.sapGray5, mb: 0.5 }}>Job Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: SAP_COLORS.sapGray7 }}>
                  {selectedJobToClone?.[2] || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: SAP_COLORS.sapGray5, mb: 0.5 }}>Client</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: SAP_COLORS.sapGray7 }}>
                  {isAdmin ? (client?.label || '—') : (clientOptions.find((c: any) => String(c.value) === String(userClientId))?.label || '—')}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Flow Arrow */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: SAP_COLORS.sapGray2,
                color: SAP_COLORS.sapGray5,
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: 20 }} />
            </Box>
          </Box>

          {/* Destination Section */}
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 1.5,
              borderColor: SAP_COLORS.sapGray2,
              borderLeft: `4px solid ${SAP_COLORS.sapBlue}`,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: SAP_COLORS.sapGray5, mb: 2, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Destination
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              <Autocomplete
                options={clientOptions}
                value={clientOptions.find((c: any) => String(c.value) === String(newClientId)) || null}
                onChange={(_, v) => setNewClientId(v?.value ?? '')}
                getOptionLabel={(option) => option?.label || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Destination Client"
                    placeholder="Select destination client"
                    size="small"
                  />
                )}
                ListboxProps={{ style: { maxHeight: 200 } }}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
              />
              <TextField
                label="New Job Name"
                value={newJobName}
                onChange={handleJobNameChange}
                placeholder="e.g. MyJob_Clone"
                size="small"
                fullWidth
                error={!!jobNameError}
                helperText={jobNameError || 'Letters and underscores only'}
              />
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            px: 3,
            gap: 1,
            borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
            bgcolor: SAP_COLORS.sapGray1,
          }}
        >
          <Button
            onClick={() => setCloneDialogOpen(false)}
            disabled={cloning}
            sx={{ color: SAP_COLORS.sapGray6, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCloneJob}
            variant="contained"
            disabled={cloning || !newClientId || !newJobName || !!jobNameError}
            startIcon={cloning ? <CircularProgress size={18} color="inherit" /> : <CloneIcon />}
            sx={{
              textTransform: 'none',
              bgcolor: SAP_COLORS.sapBlue,
              '&:hover': { bgcolor: SAP_COLORS.sapDarkBlue },
            }}
          >
            {cloning ? 'Cloning...' : 'Clone'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 